import React, { useEffect, useState } from "react";
import FoodShell from "../../layouts/FoodShell";
import { appModules } from "../../components/global";
import { GET, PUT, Select, alert, loading } from "../../components/CustomComponent";
import { goalOptions, genderOptions, activityOptions, locationOptions, purchaseOptions } from "./mockData";
import { Panel, Section, Chip, KpiStrip, TagInput, Skeleton } from "./FoodUi";

const toForm = (data) => ({
  age: data.age ?? "",
  gender: data.gender || "",
  height_cm: data.height_cm ?? "",
  weight_kg: data.weight_kg ?? "",
  goal: data.goal || "",
  activity_level: data.activity_level || "",
  budget_min: data.budget_min ?? "",
  budget_max: data.budget_max ?? "",
  likes: data.likes || [],
  dislikes: data.dislikes || [],
  allergies: data.allergies || [],
  meal_breakfast: data.meal_breakfast || "",
  meal_lunch: data.meal_lunch || "",
  meal_dinner: data.meal_dinner || "",
  location: data.location || "",
  purchase: data.purchase || [],
});

// โปรไฟล์การกิน: GET/PUT v1/food/profile backend คำนวณเป้าหมาย (Mifflin-St Jeor) ให้ทุกครั้งที่บันทึก
export default function FoodProfile() {
  const { title, description } = appModules["food_profile"];
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const set = (key, value) => setForm({ ...form, [key]: value });
  const togglePurchase = (value) => set("purchase", form.purchase.includes(value) ? form.purchase.filter((item) => item !== value) : [...form.purchase, value]);

  const load = async () => {
    const result = await GET("v1/food/profile");
    if (result?.status === true) {
      setData(result.data);
      setForm(toForm(result.data));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const body = {};
    Object.entries(form).forEach(([key, value]) => {
      if (Array.isArray(value)) body[key] = value;
      else if (value !== "" && value !== null) body[key] = ["age", "height_cm", "weight_kg", "budget_min", "budget_max"].includes(key) ? Number(value) : value;
    });
    if (body.budget_min && body.budget_max && body.budget_min > body.budget_max) {
      await alert("", "warning", "แจ้งเตือน", "งบต่อมื้อต่ำสุดต้องไม่มากกว่าสูงสุด");
      return;
    }
    loading(true);
    // keepEmpty: array ว่าง (ล้างรายการชอบ/ไม่ชอบ) ต้องส่งขึ้นไปจริง
    const result = await PUT("v1/food/profile", body, null, { keepEmpty: true });
    loading(false);
    if (result?.status === true) {
      setData(result.data);
      setForm(toForm(result.data));
      alert("", "success", "บันทึกแล้ว", `เป้าหมายใหม่ ${result.data.targets.kcal.toLocaleString()} kcal / วัน โปรตีน ${result.data.targets.protein} g AI จะใช้ข้อมูลนี้ในการแนะนำมื้อถัดไป`);
    }
  };

  // ตัวเลขฝั่งขวาคำนวณสดจากฟอร์ม (สูตรเดียวกับ backend) ให้เห็นผลทันทีก่อนกดบันทึก
  const height = Number(form?.height_cm) || 0;
  const weight = Number(form?.weight_kg) || 0;
  const age = Number(form?.age) || 0;
  const bmi = height && weight ? (weight / (height / 100) ** 2).toFixed(1) : "-";
  const bmr = height && weight && age ? Math.round(10 * weight + 6.25 * height - 5 * age + (form.gender === "female" ? -161 : 5)) : 0;
  const activityFactor = { "0": 1.2, "1": 1.375, "3": 1.55, "5": 1.725 }[form?.activity_level] || 1.2;
  const tdee = Math.round(bmr * activityFactor);
  const target = tdee ? Math.max(1200, form.goal === "lose" ? tdee - 500 : form.goal === "gain" ? tdee + 300 : tdee) : 0;
  const proteinPerKg = form?.goal === "gain" ? 1.8 : form?.goal === "lose" ? 1.6 : 1.4;
  const bmiTone = bmi === "-" ? "muted" : bmi < 18.5 ? "blue" : bmi < 25 ? "green" : bmi < 30 ? "gold" : "red";
  const bmiLabel = bmi === "-" ? "-" : bmi < 18.5 ? "น้ำหนักน้อย" : bmi < 25 ? "ปกติ" : bmi < 30 ? "น้ำหนักเกิน" : "อ้วน";

  const fields = ["age", "gender", "height_cm", "weight_kg", "goal", "activity_level", "budget_min", "budget_max", "likes", "dislikes", "allergies", "meal_breakfast", "meal_lunch", "meal_dinner", "location", "purchase"];
  const filled = form ? fields.filter((key) => (Array.isArray(form[key]) ? form[key].length > 0 : String(form[key] ?? "").trim() !== "")).length : 0;
  const completeness = Math.round((filled / fields.length) * 100);

  return (
    <FoodShell title={title} description={description}>
      <div className="row g-3">
        <div className="col-xl-8">
          <Panel
            eyebrow="Profile"
            icon="fa-solid fa-user-gear"
            title="โปรไฟล์การกิน"
            desc="ไม่ต้องกรอกทั้งหมดตั้งแต่แรก ระบบจะถามทีละนิดบนหน้าแรกและเรียนรู้จากการใช้งาน"
            actions={<button type="button" className="btn stg-btn stg-btn--primary" disabled={!form} onClick={save}><i className="fa-solid fa-floppy-disk" />บันทึกโปรไฟล์</button>}
          >
            {!form ? <Skeleton lines={8} /> : (
              <>
                <Section icon="fa-solid fa-id-card" title="ข้อมูลพื้นฐาน" desc="ใช้คำนวณพลังงานที่ควรได้รับต่อวัน">
                  <div className="row g-3">
                    <div className="col-6 col-md-3"><label className="stg-label">อายุ (ปี)</label><input type="number" className="form-control" value={form.age} onChange={(event) => set("age", event.target.value)} /></div>
                    <div className="col-6 col-md-3"><label className="stg-label">เพศ</label><Select className="form-control" value={form.gender} options={genderOptions} placeholder="เลือก" onChange={(value) => set("gender", value)} /></div>
                    <div className="col-6 col-md-3"><label className="stg-label">ส่วนสูง (cm)</label><input type="number" className="form-control" value={form.height_cm} onChange={(event) => set("height_cm", event.target.value)} /></div>
                    <div className="col-6 col-md-3"><label className="stg-label">น้ำหนัก (kg)</label><input type="number" className="form-control" value={form.weight_kg} onChange={(event) => set("weight_kg", event.target.value)} /></div>
                  </div>
                </Section>

                <Section icon="fa-solid fa-bullseye" title="เป้าหมายและกิจกรรม" desc="กำหนดทิศทางของคำแนะนำ เช่น ลดน้ำหนักจะเน้นเมนูแคลต่ำโปรตีนสูง">
                  <div className="row g-3">
                    <div className="col-md-4"><label className="stg-label">เป้าหมาย</label><Select className="form-control" value={form.goal} options={goalOptions} placeholder="เลือก" onChange={(value) => set("goal", value)} /></div>
                    <div className="col-md-4"><label className="stg-label">ออกกำลังกาย</label><Select className="form-control" value={form.activity_level} options={activityOptions} placeholder="เลือก" onChange={(value) => set("activity_level", value)} /></div>
                    <div className="col-md-4">
                      <label className="stg-label">งบต่อมื้อ (บาท)</label>
                      <div className="food-range">
                        <input type="number" className="form-control" value={form.budget_min} placeholder="ต่ำสุด" onChange={(event) => set("budget_min", event.target.value)} />
                        <span>–</span>
                        <input type="number" className="form-control" value={form.budget_max} placeholder="สูงสุด" onChange={(event) => set("budget_max", event.target.value)} />
                      </div>
                    </div>
                  </div>
                </Section>

                <Section icon="fa-solid fa-heart" title="ความชอบและข้อจำกัด" desc="พิมพ์แล้วกด Enter เพื่อเพิ่ม AI จะไม่แนะนำเมนูที่มีของที่ไม่ชอบหรือแพ้">
                  <div className="row g-3">
                    <div className="col-md-4"><label className="stg-label">อาหารที่ชอบ</label><TagInput value={form.likes} onChange={(value) => set("likes", value)} placeholder="เช่น ไก่, ปลา, อาหารคลีน" tone="green" /></div>
                    <div className="col-md-4"><label className="stg-label">อาหารที่ไม่ชอบ</label><TagInput value={form.dislikes} onChange={(value) => set("dislikes", value)} placeholder="เช่น เครื่องใน (หรือ ไม่มี)" tone="muted" /></div>
                    <div className="col-md-4"><label className="stg-label">แพ้อาหาร</label><TagInput value={form.allergies} onChange={(value) => set("allergies", value)} placeholder="เช่น กุ้ง, ถั่ว, นม (หรือ ไม่มี)" tone="red" /></div>
                  </div>
                </Section>

                <Section icon="fa-regular fa-clock" title="เวลาและสถานที่" desc="ใช้แนะนำตามช่วงเวลา เช่น เปิดแอปตอนดึกจะแนะนำมื้อเบา ๆ">
                  <div className="row g-3">
                    <div className="col-4 col-md-2"><label className="stg-label">มื้อเช้า</label><input type="time" className="form-control" value={form.meal_breakfast} onChange={(event) => set("meal_breakfast", event.target.value)} /></div>
                    <div className="col-4 col-md-2"><label className="stg-label">มื้อกลางวัน</label><input type="time" className="form-control" value={form.meal_lunch} onChange={(event) => set("meal_lunch", event.target.value)} /></div>
                    <div className="col-4 col-md-2"><label className="stg-label">มื้อเย็น</label><input type="time" className="form-control" value={form.meal_dinner} onChange={(event) => set("meal_dinner", event.target.value)} /></div>
                    <div className="col-md-3"><label className="stg-label">สถานที่หลัก</label><Select className="form-control" value={form.location} options={locationOptions} placeholder="เลือก" onChange={(value) => set("location", value)} /></div>
                    <div className="col-md-3">
                      <label className="stg-label">วิธีซื้อ (เลือกได้หลายข้อ)</label>
                      <div className="d-flex flex-wrap gap-1">
                        {purchaseOptions.map((item) => (
                          <Chip key={item.value} tone="orange" icon={item.icon} active={form.purchase.includes(item.value)} onClick={() => togglePurchase(item.value)}>{item.label}</Chip>
                        ))}
                      </div>
                    </div>
                  </div>
                </Section>
              </>
            )}
          </Panel>
        </div>

        <div className="col-xl-4">
          <Section icon="fa-solid fa-circle-half-stroke" title="ความครบของโปรไฟล์" desc="ยิ่งครบ AI ยิ่งแนะนำได้ตรงใจ" className="mb-3">
            <div className="food-complete">
              <div className="food-complete-ring" style={{ "--p": completeness }}><span>{completeness}%</span></div>
              <div>
                <div className="fw-semibold">กรอกแล้ว {filled} จาก {fields.length} รายการ</div>
                {data?.next_question ? <div className="food-complete-ask"><i className="fa-solid fa-robot" />คำถามถัดไปที่ AI จะถามบนหน้าแรก: <strong>{data.next_question.title}</strong></div> : <div className="text-secondary small">ครบแล้ว ระบบจะเรียนรู้เพิ่มจากการกดชอบ/ไม่เอา</div>}
              </div>
            </div>
          </Section>

          <Section icon="fa-solid fa-calculator" title="เป้าหมายพลังงานของคุณ" desc="คำนวณจากข้อมูลพื้นฐาน (Mifflin-St Jeor) ปรับตามเป้าหมาย" className="mb-3">
            <KpiStrip
              className="food-kpis--2"
              items={[
                [bmiTone, "fa-solid fa-weight-scale", bmi, `BMI · ${bmiLabel}`],
                ["orange", "fa-solid fa-fire", tdee ? tdee.toLocaleString() : "-", "kcal ที่เผาผลาญ/วัน"],
                ["green", "fa-solid fa-bullseye", target ? target.toLocaleString() : "-", "kcal เป้าหมาย/วัน"],
                ["red", "fa-solid fa-drumstick-bite", weight ? `${Math.round(weight * proteinPerKg)} g` : "-", "โปรตีนเป้าหมาย/วัน"],
              ]}
            />
            <p className="food-tip mt-3"><i className="fa-solid fa-robot" />{target ? <>เป้าหมาย "{goalOptions.find((item) => item.value === form?.goal)?.label || "ทั่วไป"}" ระบบตั้งเป้าที่ <strong>{target.toLocaleString()} kcal</strong> ต่อวัน และแนะนำมื้อละไม่เกิน {Math.round(target / 3).toLocaleString()} kcal</> : <>กรอกอายุ เพศ ส่วนสูง น้ำหนัก ระบบจะคำนวณเป้าหมายให้ทันที</>}</p>
          </Section>

          {data?.learned?.insights?.length ? (
            <Section icon="fa-solid fa-brain" title="AI เรียนรู้ว่า" desc={data.learned.sample ? `จาก ${data.learned.sample.logs} มื้อ และการตอบกลับ ${data.learned.sample.feedbacks} ครั้ง` : ""}>
              <div className="d-grid gap-2">
                {data.learned.insights.map((item) => (
                  <div key={item.text} className="food-insight">
                    <span className="food-insight-icon"><i className={item.icon} /></span>
                    <div><strong>{item.text}</strong><small>{item.detail}</small></div>
                  </div>
                ))}
              </div>
            </Section>
          ) : null}
        </div>
      </div>
    </FoodShell>
  );
}
